import React, { useMemo, useRef, useState } from 'react';
import { fmtPct, signClass } from '../../utils/format';
import './PriceChart.css';

// Several value series rebased to 100, on one axis. Used by the backtest:
// what you hold, the optimiser's two answers and the S&P 500 over the same year.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function niceTicks(min, max, count) {
  const span = max - min || 1;
  const raw = span / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const norm = raw / mag;
  const step = (norm < 1.5 ? 1 : norm < 3 ? 2 : norm < 7 ? 5 : 10) * mag;
  const start = Math.ceil(min / step) * step;
  const ticks = [];
  for (let v = start; v <= max + 1e-9; v += step) ticks.push(Number(v.toFixed(6)));
  return ticks;
}

export default function GrowthChart({ dates = [], series = {}, labels = {}, order = [], height = 260, ariaLabel = 'Growth of 100' }) {
  const [hover, setHover] = useState(null);
  const svgRef = useRef(null);
  const W = 640;
  const H = height;
  const L = 6;
  const R = 44;
  const T = 12;
  const B = 28;

  const keys = (order.length ? order : Object.keys(series)).filter((k) => Array.isArray(series[k]) && series[k].length === dates.length);

  const model = useMemo(() => {
    const n = dates.length;
    if (n < 2 || !keys.length) return null;
    const all = keys.flatMap((k) => series[k]).filter((v) => Number.isFinite(v));
    const lo = Math.min(...all, 100);
    const hi = Math.max(...all, 100);
    const pad = (hi - lo || 1) * 0.06;
    const yMin = lo - pad;
    const yMax = hi + pad;
    const x = (i) => L + (i / (n - 1)) * (W - L - R);
    const y = (v) => T + (1 - (v - yMin) / (yMax - yMin || 1)) * (H - T - B);
    const xTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => {
      const i = Math.round(f * (n - 1));
      const [yy, mm] = dates[i].split('-').map(Number);
      return { i, label: `${MONTHS[mm - 1]} ${String(yy).slice(2)}` };
    });
    return { n, x, y, yTicks: niceTicks(yMin, yMax, 4), xTicks };
  }, [dates, series, keys, H]);

  if (!model) return null;
  const { n, x, y, yTicks, xTicks } = model;

  const onMove = (e) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const px = ((clientX - rect.left) / rect.width) * W;
    setHover(Math.max(0, Math.min(n - 1, Math.round(((px - L) / (W - L - R)) * (n - 1)))));
  };
  const hx = hover != null ? x(hover) : 0;
  const tipLeft = hover != null ? (hx / W) * 100 : 0;

  return (
    <div className="dd-growth">
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img" aria-label={ariaLabel}
        onMouseMove={onMove} onMouseLeave={() => setHover(null)} onTouchStart={onMove} onTouchMove={onMove} onTouchEnd={() => setHover(null)}>
        <g className="dd-growth-grid">{yTicks.map((t) => <line key={t} x1={L} x2={W - R} y1={y(t)} y2={y(t)} />)}</g>
        <line className="dd-growth-base" x1={L} x2={W - R} y1={y(100)} y2={y(100)} />
        <g className="dd-growth-ticks dd-mono">
          {yTicks.map((t) => <text key={`y${t}`} x={W - R + 8} y={y(t) + 4}>{t}</text>)}
          {xTicks.map((t) => <text key={`x${t.i}`} x={x(t.i)} y={H - 8} textAnchor={t.i === 0 ? 'start' : t.i === n - 1 ? 'end' : 'middle'}>{t.label}</text>)}
        </g>
        {keys.map((k) => (
          <path key={k} className={`dd-growth-line dd-growth-line--${k}`} d={series[k].map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join('')} />
        ))}
        {hover != null && <g className="dd-growth-hover"><line x1={hx} x2={hx} y1={T} y2={H - B} /></g>}
      </svg>
      {hover != null && (
        <div className={`dd-price-tip dd-mono ${tipLeft > 55 ? 'left' : ''}`} style={{ left: `${tipLeft}%` }} role="status">
          <div className="dd-price-tip-date">{dates[hover]}</div>
          {keys.map((k) => (
            <div key={k} className="dd-price-tip-row"><span>{labels[k] || k}</span><span className={signClass(series[k][hover] - 100)}>{fmtPct(series[k][hover] - 100)}</span></div>
          ))}
        </div>
      )}
      <div className="dd-growth-legend">
        {keys.map((k) => <span key={k} className={k}>{labels[k] || k} <span className={`dd-mono ${signClass(series[k][n - 1] - 100)}`}>{fmtPct(series[k][n - 1] - 100)}</span></span>)}
      </div>
    </div>
  );
}
